import React, { useState, useEffect, useCallback } from 'react';
import { useParams } from 'react-router-dom';
import { getToken } from '../utils/auth';
import './GeneratedCertsPage.css';

const API = process.env.REACT_APP_API_URL || 'http://localhost:5001/api';

const CERT_TYPE_LABELS = {
  SLC: 'School Leaving',
  TC: 'Transfer Certificate',
  BONAFIDE: 'Bonafide',
  CHARACTER: 'Character',
};

const GeneratedCertsPage = () => {
  const { schoolId } = useParams();
  const [certificates, setCertificates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [filterType, setFilterType] = useState('ALL');
  const [searchTerm, setSearchTerm] = useState('');

  const token = getToken(schoolId);

  const fetchCertificates = useCallback(async () => {
    try {
      setLoading(true);
      const res = await fetch(`${API}/certificates/all/list`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const data = await res.json();
      if (data.success) {
        setCertificates(data.data || []);
      } else {
        setError(data.message || 'Failed to load certificates');
      }
    } catch (err) {
      setError('Network error');
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => {
    fetchCertificates();
  }, [fetchCertificates]);

  const handleDelete = async (cert) => {
    if (!window.confirm(`Delete ${CERT_TYPE_LABELS[cert.Certificate_Type]} certificate for ${cert.Student_Name}?`)) return;
    try {
      const type = cert.Certificate_Type.toLowerCase();
      const res = await fetch(`${API}/certificates/${type}/${cert.Certificate_Id}`, {
        method: 'DELETE',
        headers: { Authorization: `Bearer ${token}` },
      });
      const data = await res.json();
      if (data.success) {
        setCertificates(prev => prev.filter(c => c.Certificate_Id !== cert.Certificate_Id));
      } else {
        setError(data.message || 'Delete failed');
      }
    } catch {
      setError('Network error');
    }
  };

  const filtered = certificates.filter(c => {
    if (filterType !== 'ALL' && c.Certificate_Type !== filterType) return false;
    if (searchTerm) {
      const term = searchTerm.toLowerCase();
      return (
        (c.Student_Name || '').toLowerCase().includes(term) ||
        (c.Certificate_Number || '').toLowerCase().includes(term) ||
        (c.Certificate_Id || '').toLowerCase().includes(term)
      );
    }
    return true;
  });

  return (
    <div className="gencerts-page">
      <div className="gencerts-header">
        <h1 className="page-title">Generated Certificates</h1>
        <span className="gencerts-count">{filtered.length} certificate{filtered.length !== 1 ? 's' : ''}</span>
      </div>

      {error && (
        <div className="docgen-alert docgen-alert-error">
          <span>{error}</span>
          <button onClick={() => setError('')}>&times;</button>
        </div>
      )}

      <div className="gencerts-toolbar">
        <input
          type="text"
          className="docgen-input gencerts-search"
          placeholder="Search by student name or certificate number..."
          value={searchTerm}
          onChange={e => setSearchTerm(e.target.value)}
        />
        <select
          className="docgen-input gencerts-filter"
          value={filterType}
          onChange={e => setFilterType(e.target.value)}
        >
          <option value="ALL">All Types</option>
          <option value="SLC">School Leaving</option>
          <option value="TC">Transfer Certificate</option>
          <option value="BONAFIDE">Bonafide</option>
          <option value="CHARACTER">Character</option>
        </select>
        <button className="docgen-btn docgen-btn-secondary" onClick={fetchCertificates}>
          Refresh
        </button>
      </div>

      {loading ? (
        <div className="gencerts-loading">
          <div className="dash-spinner" />
          <span>Loading certificates...</span>
        </div>
      ) : filtered.length === 0 ? (
        <div className="gencerts-empty">
          <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
            <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
            <polyline points="14 2 14 8 20 8" />
          </svg>
          <p>No certificates found</p>
        </div>
      ) : (
        <div className="gencerts-table-wrap">
          <table className="gencerts-table">
            <thead>
              <tr>
                <th>#</th>
                <th>Type</th>
                <th>Cert No.</th>
                <th>Student ID</th>
                <th>Adm. No.</th>
                <th>Student</th>
                <th>Class</th>
                <th>Date</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {filtered.map((cert, idx) => (
                <tr key={cert.Certificate_Id}>
                  <td>{idx + 1}</td>
                  <td>
                    <span className={`gencerts-badge gencerts-badge-${cert.Certificate_Type.toLowerCase()}`}>
                      {CERT_TYPE_LABELS[cert.Certificate_Type] || cert.Certificate_Type}
                    </span>
                  </td>
                  <td className="gencerts-mono">{cert.Certificate_Number}</td>
                  <td className="gencerts-mono">{cert.Student_Id}</td>
                  <td className="gencerts-mono">{cert.Admission_Number}</td>
                  <td>{cert.Student_Name}</td>
                  <td>{cert.Student_Class}</td>
                  <td>{cert.Certificate_Date ? new Date(cert.Certificate_Date).toLocaleDateString() : '—'}</td>
                  <td>
                    <button
                      className="docgen-btn-sm gencerts-btn-view"
                      onClick={() => {
                        const type = cert.Certificate_Type.toLowerCase();
                        window.open(`/${schoolId}/${type === 'character' ? 'bonafide' : type}`, '_blank');
                      }}
                    >
                      View
                    </button>
                    <button
                      className="docgen-btn-sm gencerts-btn-delete"
                      onClick={() => handleDelete(cert)}
                    >
                      Delete
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

export default GeneratedCertsPage;
